import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, GraduationCap, Sun, Briefcase, Factory, Building2, Recycle, Cloud, Handshake, ChevronDown, FileText, Download } from "lucide-react";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { useT } from "@/i18n/helpers";
import Seo from "@/components/Seo";

const Section = ({ children, className = "", id }) => (
 <section id={id} className={`border-t border-nanda-line ${className}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28">{children}</div>
 </section>
);
const Overline = ({ children }) => (<div className="label-overline text-nanda-muted mb-4">{children}</div>);
const OddBadge = ({ num, color }) => (
 <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-white text-xs font-medium tracking-wide" style={{ backgroundColor: color }} aria-label={`ODD ${num}`}>
 <span className="font-serif text-sm tabular-nums">{String(num).padStart(2, "0")}</span>
 <span className="uppercase text-[10px] tracking-widest opacity-90">ODD</span>
 </div>
);

export default function EngagementODD() {
 const tt = useT();

 const ODDS = [
 { num: 4, color: "#C5192D", icon: GraduationCap,
 title: tt("Éducation de qualité", "Quality education", "Educação de qualidade"),
 lead: tt("Avec NANDA SÁ Académie, nous agissons sur l'orientation, la formation et le développement des compétences.",
 "Through NANDA SÁ Académie, we act on career guidance, training and skills development.",
 "Com a NANDA SÁ Académie, agimos sobre a orientação, a formação e o desenvolvimento de competências."),
 actions: [
 tt("Orientation scolaire et professionnelle", "School and career guidance", "Orientação escolar e profissional"),
 tt("Culture numérique et sensibilisation aux solutions durables", "Digital literacy and awareness of sustainable solutions", "Cultura digital e sensibilização para soluções sustentáveis"),
 tt("Accompagnement des jeunes, des établissements et des porteurs de projets", "Support for young people, schools and project leaders", "Acompanhamento de jovens, estabelecimentos e promotores de projectos"),
 tt("Programmes co-construits avec écoles, collectivités et entreprises", "Programmes co-built with schools, local authorities and businesses", "Programas co-construídos com escolas, autarquias e empresas"),
 ],
 link: "/marques/academie",
 linkLabel: tt("Voir NANDA SÁ Académie", "See NANDA SÁ Académie", "Ver a NANDA SÁ Académie") },
 { num: 7, color: "#FCC30B", icon: Sun,
 title: tt("Énergie propre et d'un coût abordable", "Affordable and clean energy", "Energia limpa e a preço acessível"),
 lead: tt("L'accès à une énergie fiable et propre est un accélérateur direct de développement.",
 "Access to reliable, clean energy is a direct accelerator of development.",
 "O acesso a uma energia fiável e limpa é um acelerador directo de desenvolvimento."),
 actions: [
 tt("Solutions solaires résidentielles, tertiaires et industrielles", "Residential, commercial and industrial solar solutions", "Soluções solares residenciais, terciárias e industriais"),
 tt("Stockage lithium et solutions autonomes hors-réseau", "Lithium storage and off-grid autonomous solutions", "Armazenamento de lítio e soluções autónomas fora da rede"),
 tt("Efficacité énergétique et audits pour les entreprises", "Energy efficiency and audits for businesses", "Eficiência energética e auditorias para as empresas"),
 tt("Pompage solaire et éclairage public autonome", "Solar pumping and autonomous street lighting", "Bombagem solar e iluminação pública autónoma"),
 ],
 link: "/energies-renouvelables", linkLabel: tt("Voir Énergies renouvelables", "See Renewable energy", "Ver Energias renováveis") },
 { num: 8, color: "#A21942", icon: Briefcase,
 title: tt("Travail décent et croissance économique", "Decent work and economic growth", "Trabalho digno e crescimento económico"),
 lead: tt("Nous accompagnons la création d'opportunités économiques ancrées localement.",
 "We support the creation of locally rooted economic opportunities.",
 "Acompanhamos a criação de oportunidades económicas enraizadas localmente."),
 actions: [
 tt("Développement des filières agricoles et industrielles", "Development of agricultural and industrial value chains", "Desenvolvimento das fileiras agrícolas e industriais"),
 tt("Appui aux entreprises et aux investisseurs", "Support for companies and investors", "Apoio às empresas e aos investidores"),
 tt("Structuration de partenariats commerciaux responsables", "Structuring of responsible commercial partnerships", "Estruturação de parcerias comerciais responsáveis"),
 tt("Emplois formés et durables dans le froid, le solaire et la maintenance", "Trained, sustainable jobs in refrigeration, solar and maintenance", "Empregos formados e sustentáveis no frio, no solar e na manutenção"),
 ],
 link: "/conseil-implantation", linkLabel: tt("Voir Conseil & implantation", "See Consulting & establishment", "Ver Consultoria e implantação") },
 { num: 9, color: "#FD6925", icon: Factory,
 title: tt("Industrie, innovation et infrastructures", "Industry, innovation and infrastructure", "Indústria, inovação e infra-estruturas"),
 lead: tt("Notre cœur de métier historique : équiper l'Afrique de l'Ouest en infrastructures techniques fiables.",
 "Our historical core business: equipping West Africa with reliable technical infrastructure.",
 "O nosso ofício histórico central: equipar a África Ocidental com infra-estruturas técnicas fiáveis."),
 actions: [
 tt("Réfrigération industrielle et chaîne du froid", "Industrial refrigeration and cold chain", "Refrigeração industrial e cadeia de frio"),
 tt("Climatisation professionnelle et centralisée", "Professional and centralised air conditioning", "Climatização profissional e centralizada"),
 tt("Chambres froides pour l'agroalimentaire, la santé et la logistique", "Cold rooms for food industry, health and logistics", "Câmaras frias para agro-alimentar, saúde e logística"),
 tt("Infrastructures énergétiques modernes et innovation technique", "Modern energy infrastructure and technical innovation", "Infra-estruturas energéticas modernas e inovação técnica"),
 ],
 link: "/refrigeration-climatisation", linkLabel: tt("Voir Réfrigération & climatisation", "See Refrigeration & AC", "Ver Refrigeração e climatização") },
 { num: 11, color: "#FD9D24", icon: Building2,
 title: tt("Villes et communautés durables", "Sustainable cities and communities", "Cidades e comunidades sustentáveis"),
 lead: tt("Nous contribuons à des territoires mieux équipés, plus résilients et plus durables.",
 "We contribute to better-equipped, more resilient and more sustainable territories.",
 "Contribuímos para territórios mais bem equipados, mais resilientes e mais sustentáveis."),
 actions: [
 tt("Solutions énergétiques durables pour bâtiments et collectivités", "Sustainable energy solutions for buildings and communities", "Soluções energéticas sustentáveis para edifícios e autarquias"),
 tt("Infrastructures résilientes face au climat tropical", "Infrastructure resilient to tropical climate", "Infra-estruturas resilientes face ao clima tropical"),
 tt("Développement territorial équilibré Guinée-Bissau", "Balanced territorial development Guinea-Bissau", "Desenvolvimento territorial equilibrado Guiné-Bissau"),
 ],
 link: "/conseil-implantation", linkLabel: tt("Voir Conseil", "See Consulting", "Ver Consultoria") },
 { num: 12, color: "#BF8B2E", icon: Recycle,
 title: tt("Consommation et production responsables", "Responsible consumption and production", "Consumo e produção responsáveis"),
 lead: tt("Valoriser localement plutôt qu'exporter la matière brute, choisir des équipements durables.",
 "Valorising locally rather than exporting the raw material, choosing durable equipment.",
 "Valorizar localmente em vez de exportar a matéria em bruto, escolher equipamentos duradouros."),
 actions: [
 tt("Valorisation des matières premières naturelles ouest-africaines", "Valorisation of West African natural raw materials", "Valorização das matérias-primas naturais oeste-africanas"),
 tt("Développement de chaînes de valeur locales (cajou, sel, miel, plantes)", "Development of local value chains (cashew, salt, honey, plants)", "Desenvolvimento de cadeias de valor locais (caju, sal, mel, plantas)"),
 tt("Promotion d'équipements performants, économes et durables", "Promotion of high-performance, efficient and durable equipment", "Promoção de equipamentos performantes, económicos e duradouros"),
 ],
 link: "/matieres-premieres-naturelles", linkLabel: tt("Voir Matières premières naturelles", "See Natural raw materials", "Ver Matérias-primas naturais") },
 { num: 13, color: "#3F7E44", icon: Cloud,
 title: tt("Lutte contre les changements climatiques", "Climate action", "Acção climática"),
 lead: tt("Notre offre solaire et frigorifique participe directement à la réduction des émissions.",
 "Our solar and refrigeration offer directly contributes to reducing emissions.",
 "A nossa oferta solar e frigorífica participa directamente na redução das emissões."),
 actions: [
 tt("Réduction des émissions grâce aux énergies renouvelables", "Emissions reduction through renewable energy", "Redução das emissões graças às energias renováveis"),
 tt("Amélioration de l'efficacité énergétique dans les bâtiments et industries", "Energy efficiency improvements in buildings and industry", "Melhoria da eficiência energética em edifícios e indústrias"),
 tt("Solutions de réfrigération plus performantes (moins de fuites, meilleure isolation)", "Higher-performance refrigeration solutions (fewer leaks, better insulation)", "Soluções de refrigeração mais performantes (menos fugas, melhor isolamento)"),
 ],
 link: "/energies-renouvelables", linkLabel: tt("Voir Énergies renouvelables", "See Renewable energy", "Ver Energias renováveis") },
 { num: 17, color: "#19486A", icon: Handshake,
 title: tt("Partenariats pour la réalisation des objectifs", "Partnerships for the goals", "Parcerias para os objectivos"),
 lead: tt("Rien ne se construit seul. Nos actions se déploient avec l'ensemble de l'écosystème.",
 "Nothing is built alone. Our actions unfold with the entire ecosystem.",
 "Nada se constrói sozinho. As nossas acções desenvolvem-se com todo o ecossistema."),
 actions: [
 tt("Coopération avec entreprises, collectivités et ONG", "Cooperation with businesses, local authorities and NGOs", "Cooperação com empresas, autarquias e ONG"),
 tt("Partenariats avec les établissements d'enseignement et les institutions", "Partnerships with educational institutions and public bodies", "Parcerias com estabelecimentos de ensino e instituições"),
 tt("Développement de partenariats internationaux (Europe, Afrique, Moyen-Orient)", "Development of international partnerships (Europe, Africa, Middle East)", "Desenvolvimento de parcerias internacionais (Europa, África, Médio Oriente)"),
 tt("Mise en relation entre producteurs, transformateurs et acheteurs", "Matchmaking between producers, processors and buyers", "Mise en relation entre produtores, transformadores e compradores"),
 ],
 link: "/filiere-cajou", linkLabel: tt("Voir Filière cajou", "See Cashew value chain", "Ver Fileira do caju") },
 ];

 return (
 <div data-testid="page-engagement-odd">
 <Seo />
 <section className="relative min-h-[70vh] w-full overflow-hidden bg-nanda-ink flex items-center">
 <div className="absolute inset-0 bg-gradient-to-br from-nanda-ink via-nanda-ink to-[#1a2b3d]" />
 <div className="absolute inset-0 opacity-15 pointer-events-none">
 <div className="grid grid-cols-4 md:grid-cols-8 h-full w-full">
 {ODDS.map((o) => (<div key={o.num} style={{ backgroundColor: o.color }} className="h-full" />))}
 </div>
 </div>
 <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-28 md:py-40 w-full">
 <FadeUp>
 <div className="label-overline text-white/85 mb-6">{tt("Objectifs de Développement Durable · ONU", "Sustainable Development Goals · UN", "Objectivos de Desenvolvimento Sustentável · ONU")}</div>
 <h1 className="font-serif text-white text-5xl md:text-7xl lg:text-[88px] leading-[1.02] max-w-5xl">
 {tt("Notre engagement pour", "Our commitment to", "O nosso compromisso com")}<br />
 <span className="text-nanda-gold italic">{tt("les Objectifs de Développement Durable.", "the Sustainable Development Goals.", "os Objectivos de Desenvolvimento Sustentável.")}</span>
 </h1>
 <p className="mt-8 max-w-3xl text-white/85 text-lg md:text-xl leading-relaxed">
 {tt("À travers ses activités industrielles, énergétiques, éducatives et commerciales, NANDA SÁ contribue à huit Objectifs de Développement Durable des Nations Unies. Voici comment, concrètement.",
 "Through its industrial, energy, educational and commercial activities, NANDA SÁ contributes to eight UN Sustainable Development Goals. Here is how, concretely.",
 "Através das suas actividades industriais, energéticas, educativas e comerciais, a NANDA SÁ contribui para oito Objectivos de Desenvolvimento Sustentável das Nações Unidas. Eis como, concretamente.")}
 </p>
 <div className="mt-10">
 <a href="#odds" className="inline-flex items-center gap-3 border border-white/50 text-white px-8 py-4 label-overline hover:bg-white hover:text-nanda-ink transition-colors duration-500" data-testid="odd-cta-scroll">
 {tt("Voir les 8 ODD prioritaires", "See the 8 priority SDGs", "Ver os 8 ODS prioritários")} <ChevronDown size={16} strokeWidth={1.5} />
 </a>
 </div>
 </FadeUp>
 </div>
 </section>

 <Section>
 <div className="grid grid-cols-1 md:grid-cols-12 gap-14">
 <FadeUp className="md:col-span-5">
 <Overline>{tt("Un engagement concret", "A concrete commitment", "Um compromisso concreto")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Huit objectifs, une même intention.", "Eight goals, one same intention.", "Oito objectivos, uma mesma intenção.")}
 </h2>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-7 text-nanda-inkSoft text-[17px] leading-relaxed space-y-5">
 <p>{tt("Les Objectifs de Développement Durable (ODD) constituent le cadre international de référence pour un développement économique, social et environnemental équilibré, à l'horizon 2030.",
 "The Sustainable Development Goals (SDGs) are the international reference framework for balanced economic, social and environmental development, with a 2030 horizon.",
 "Os Objectivos de Desenvolvimento Sustentável (ODS) constituem o quadro internacional de referência para um desenvolvimento económico, social e ambiental equilibrado, no horizonte 2030.")}</p>
 <p>{tt("NANDA SÁ ne prétend pas contribuer à tous les ODD. Nous préférons mettre en avant uniquement ceux qui sont directement liés à nos activités quotidiennes, sans exagérer ni revendiquer d'impact non démontré.",
 "NANDA SÁ does not claim to contribute to all SDGs. We prefer to highlight only those directly linked to our daily activities, without exaggerating or claiming undemonstrated impact.",
 "A NANDA SÁ não pretende contribuir para todos os ODS. Preferimos destacar apenas aqueles directamente ligados às nossas actividades quotidianas, sem exagerar nem reivindicar impacto não demonstrado.")}</p>
 <p>{tt("Notre engagement est concret : il se lit dans nos chantiers, dans nos partenariats, dans la formation que nous soutenons, et dans les filières que nous accompagnons en Guinée-Bissau et en Afrique de l'Ouest.",
 "Our commitment is concrete: it can be read in our projects, our partnerships, the training we support and the value chains we accompany in Guinea-Bissau and West Africa.",
 "O nosso compromisso é concreto: lê-se nas nossas obras, nas nossas parcerias, na formação que apoiamos e nas fileiras que acompanhamos na Guiné-Bissau e na África Ocidental.")}</p>
 </FadeUp>
 </div>
 </Section>

 <Section id="odds" className="bg-nanda-bgAlt">
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>{tt("Nos ODD prioritaires", "Our priority SDGs", "Os nossos ODS prioritários")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Huit ODD directement liés à nos activités.", "Eight SDGs directly linked to our activities.", "Oito ODS directamente ligados às nossas actividades.")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
 {ODDS.map((o) => {
 const Icon = o.icon;
 return (
 <StaggerItem key={o.num}>
 <article data-testid={`odd-${o.num}`} className="bg-white border border-nanda-line p-8 md:p-10 h-full flex flex-col hover:shadow-lg transition-shadow duration-500 group">
 <div className="flex items-start justify-between mb-6 gap-4">
 <OddBadge num={o.num} color={o.color} />
 <div className="w-14 h-14 flex items-center justify-center shrink-0 transition-transform duration-500 group-hover:scale-110" style={{ backgroundColor: o.color }} aria-hidden="true">
 <Icon size={26} strokeWidth={1.4} className="text-white" />
 </div>
 </div>
 <h3 className="font-serif text-2xl md:text-3xl text-nanda-ink leading-[1.15] mb-4">{o.title}</h3>
 <p className="text-[15px] text-nanda-inkSoft leading-relaxed mb-5">{o.lead}</p>
 <ul className="space-y-2.5 mb-6 grow">
 {o.actions.map((a) => (
 <li key={a} className="flex items-start gap-2.5 text-sm text-nanda-inkSoft leading-snug">
 <span className="w-1 h-1 rounded-full mt-2 shrink-0" style={{ backgroundColor: o.color }} />{a}
 </li>
 ))}
 </ul>
 <Link to={o.link} data-testid={`odd-${o.num}-link`} className="mt-auto inline-flex items-center gap-2 label-overline text-nanda-ink border-b border-nanda-ink/30 hover:border-nanda-ink transition-colors self-start pb-1">
 {o.linkLabel} <ArrowUpRight size={14} strokeWidth={1.5} />
 </Link>
 </article>
 </StaggerItem>
 );
 })}
 </Stagger>
 </Section>

 <Section>
 <div className="grid grid-cols-1 md:grid-cols-12 gap-14">
 <FadeUp className="md:col-span-5">
 <Overline>{tt("Notre parti-pris", "Our stance", "O nosso partido")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("La sobriété plutôt que la promesse.", "Sobriety over promises.", "A sobriedade em vez da promessa.")}
 </h2>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-7 text-nanda-inkSoft text-[17px] leading-relaxed space-y-5">
 <p>{tt("Nous préférons parler de ce que nous faisons, pas de ce que nous pourrions faire. C'est pour cette raison que cette page ne revendique aucun label, aucun score et aucun impact mesuré : uniquement des activités réelles, en lien direct avec chaque ODD listé.",
 "We prefer to talk about what we do, not about what we could do. This is why this page claims no label, no score and no measured impact: only real activities, directly linked to each listed SDG.",
 "Preferimos falar do que fazemos, não do que poderíamos fazer. É por essa razão que esta página não reivindica qualquer selo, pontuação ou impacto medido: apenas actividades reais, em ligação directa com cada ODS listado.")}</p>
 <p>{tt("Notre ambition, à long terme, est de mesurer et publier des indicateurs vérifiés — nombre de jeunes accompagnés par l'Académie, kilowatts installés, tonnes de CO₂ évitées, filières structurées. En attendant, nous restons factuels.",
 "Our long-term ambition is to measure and publish verified indicators — number of young people supported by the Académie, kilowatts installed, tonnes of CO₂ avoided, structured value chains. In the meantime, we remain factual.",
 "A nossa ambição, no longo prazo, é medir e publicar indicadores verificados — número de jovens acompanhados pela Académie, quilowatts instalados, toneladas de CO₂ evitadas, fileiras estruturadas. Entretanto, permanecemos factuais.")}</p>
 <p>{tt("Chaque projet que nous menons est une occasion d'ajouter, à sa mesure, quelque chose d'utile aux territoires qui nous portent.",
 "Every project we run is an opportunity to add, in its own measure, something useful to the territories that carry us.",
 "Cada projecto que levamos é uma ocasião de acrescentar, à sua medida, algo de útil aos territórios que nos sustentam.")}</p>
 </FadeUp>
 </div>
 </Section>

 <Section className="bg-nanda-ink text-white">
 <FadeUp className="max-w-5xl mx-auto">
 <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-start">
 <div className="md:col-span-5">
 <Overline><span className="text-nanda-gold">{tt("Rapport institutionnel", "Institutional report", "Relatório institucional")}</span></Overline>
 <h3 className="font-serif text-white text-3xl md:text-5xl lg:text-[52px] leading-[1.05] mt-4">
 {tt("Notre rapport ODD", "Our SDG report", "O nosso relatório ODS")}<br />
 <span className="text-nanda-gold italic">{tt("en téléchargement libre.", "free to download.", "para descarregar livremente.")}</span>
 </h3>
 <p className="mt-6 text-white/75 text-[15px] leading-relaxed max-w-md">
 {tt(
 "Un document institutionnel de 9 pages présentant notre contribution aux 8 ODD prioritaires. Format A4, sobre, destiné aux entreprises, investisseurs, collectivités, ONG et bailleurs.",
 "A 9-page institutional document presenting our contribution to the 8 priority SDGs. A4 format, sober, intended for companies, investors, local authorities, NGOs and donors.",
 "Um documento institucional de 9 páginas apresentando o nosso contributo para os 8 ODS prioritários. Formato A4, sóbrio, destinado a empresas, investidores, autarquias, ONG e financiadores.",
 )}
 </p>
 <div className="mt-6 flex items-center gap-3 text-xs text-white/50 tracking-widest uppercase font-medium">
 <FileText size={14} strokeWidth={1.5} />
 <span>PDF · A4 · 9 pages · ~830 Ko</span>
 </div>
 </div>

 <div className="md:col-span-7">
 <div className="grid grid-cols-1 gap-3">
 {[
 { lang: "fr", label: "Français", url: "/pdf/nanda-sa-rapport-odd-fr.pdf", cta: "Télécharger (FR)" },
 { lang: "en", label: "English", url: "/pdf/nanda-sa-rapport-odd-en.pdf", cta: "Download (EN)" },
 { lang: "pt", label: "Português", url: "/pdf/nanda-sa-rapport-odd-pt.pdf", cta: "Descarregar (PT)" },
 ].map((f) => (
 <a
 key={f.lang}
 href={f.url}
 target="_blank"
 rel="noopener noreferrer"
 data-testid={`odd-report-download-${f.lang}`}
 className="group flex items-center justify-between gap-6 px-7 py-6 bg-white/[0.04] border border-white/15 hover:bg-nanda-gold hover:border-nanda-gold transition-all duration-500"
 >
 <div>
 <div className="text-xs tracking-[0.28em] uppercase text-nanda-gold group-hover:text-white/85 font-medium transition-colors duration-500">
 {tt("Édition 2026", "2026 Edition", "Edição 2026")}
 </div>
 <div className="font-serif text-white text-2xl md:text-3xl mt-1 group-hover:text-white transition-colors duration-500">
 {f.label}
 </div>
 </div>
 <div className="flex items-center gap-3 shrink-0 text-white group-hover:text-white transition-colors duration-500">
 <span className="hidden md:inline text-[11px] tracking-widest uppercase font-medium opacity-85">{f.cta}</span>
 <Download size={20} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-y-0.5" />
 </div>
 </a>
 ))}
 </div>
 <p className="mt-6 text-white/50 text-xs leading-relaxed">
 {tt(
 "Document optimisé pour l'impression professionnelle A4 et la lecture numérique. Signets, table des matières cliquable et QR code inclus.",
 "Document optimised for A4 professional printing and digital reading. Bookmarks, clickable table of contents and QR code included.",
 "Documento optimizado para impressão profissional A4 e leitura digital. Marcadores, índice clicável e QR code incluídos.",
 )}
 </p>
 <p className="mt-3 text-white/40 text-xs tracking-widest uppercase font-medium" data-testid="odd-report-last-update">
 {tt("Dernière mise à jour · Février 2026", "Last update · February 2026", "Última actualização · Fevereiro 2026")}
 </p>
 </div>
 </div>
 </FadeUp>
 </Section>

 <Section className="bg-nanda-ink text-white border-t border-white/10">
 <FadeUp className="max-w-4xl mx-auto text-center">
 <Overline><span className="text-nanda-gold">{tt("Construire ensemble", "Building together", "Construir juntos")}</span></Overline>
 <h3 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.08] mt-4">
 {tt("Un projet aligné", "A project aligned", "Um projecto alinhado")}<br />
 <span className="text-nanda-gold italic">{tt("avec les ODD ?", "with the SDGs?", "com os ODS?")}</span>
 </h3>
 <p className="mt-8 text-white/80 text-lg leading-relaxed max-w-2xl mx-auto">
 {tt("Entreprise, ONG, institution, bailleur : parlons-nous. Nos activités peuvent contribuer à vos propres engagements de développement durable.",
 "Company, NGO, institution, donor: let's talk. Our activities can contribute to your own sustainable development commitments.",
 "Empresa, ONG, instituição, financiador: falemos. As nossas actividades podem contribuir para os seus próprios compromissos de desenvolvimento sustentável.")}
 </p>
 <div className="mt-12 flex flex-wrap gap-4 justify-center">
 <Link to="/contact?subject=Engagement-ODD" className="inline-flex items-center gap-3 bg-nanda-gold text-white px-10 py-5 label-overline hover:bg-white hover:text-nanda-ink transition-colors duration-500" data-testid="odd-cta-contact">
 {tt("Nous contacter", "Contact us", "Contacte-nos")} <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 <Link to="/opportunites" className="inline-flex items-center gap-3 border border-white/50 text-white px-10 py-5 label-overline hover:bg-white hover:text-nanda-ink transition-colors duration-500" data-testid="odd-cta-opportunites">
 {tt("Voir les opportunités", "See the opportunities", "Ver as oportunidades")} <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 </div>
 </FadeUp>
 </Section>
 </div>
 );
}
